/**
 * AppointmentSettingsAdmin.tsx — Admin > Appointment Types.
 *
 * The signed-in account's appointment type groups and the types filed under
 * them, on one page (CategoryBoard), under a column header (Name | Color |
 * self-schedule | Duration | Facility) as the old Appt Types table had:
 *   - groups are listed alphabetically by name (ignoring case) and are not
 *     dragged; each group's header shows the self-schedule summary icon
 *     (orange when every active type self-schedules, grey when some do);
 *   - a group's types are listed A–Z; drag one onto another group to move it
 *     there (2026-10-07, user: the board files, it no longer orders). The
 *     move writes appointmentTypeGroupID, and atOrder puts it last in the new
 *     group's saved order (atOrder, then name).
 * Every drop saves at once. Groups and types are added, edited and (soft)
 * deleted here too, from icon buttons on the right of every row — delete
 * lives on the row only, never inside an edit modal. "Show deleted" lists the
 * deleted ones as well (muted, Restore only) and turns drag off. Single
 * account: apiClient sends the account in session; there is no account picker.
 *
 * The toolbar carries the corp filters (no account filter): Facility (a type
 * with no facility — 0, offered everywhere — always passes), Self-Schedule
 * and Confirmation (attached / none, from the group; SCRUM-2453). A searched
 * or filtered board shows only the groups holding a match, with drag off; a
 * group matching the Confirmation filter shows even with no types when
 * nothing else narrows. "Add group" sits top right in the page header, as
 * on every Admin list; types are added from a group's +.
 *
 * Deleted groups (the corp-dashboard rule, appointment-types-table.tsx): the
 * API leaves a deleted group's types pointing at it, so the page always reads
 * the deleted groups too. With Show deleted off, a type whose group is
 * deleted is hidden along with the group; with it on, the type is listed
 * under its (muted) deleted group. Only a type with no group at all — group
 * 0 / null, or an id that is neither a live nor a deleted group — goes under
 * "No group", which is shown only when it holds something.
 *
 * Separators: a group whose name has three or more hyphens in a row
 * (isSeparatorTitle, e.g. "--------") is never shown, nor are its types, in
 * either view; neither is a type whose own name is such a separator.
 *
 * API traps this page works around:
 *   - A type update writes `addon` whole, and `default_fee` / the list fields
 *     replace what is there, so an edit sends every current value.
 *   - `requires_patient_plan` is validated as a boolean, so "Patient & Plan"
 *     (2) is never sent unless the user changed it (and is not offered as a
 *     new choice).
 *   - A group update resets the stage to 1 when `stage_id` is missing, and the
 *     group list does not include it — so editing a group reads the group
 *     first and sends its stage back.
 *
 * Existing types keep the old modal's immediate self-schedule save — unless the
 * new value breaks a self-schedule rule (image, resources, products), in which
 * case it waits for "Update" like any other field.
 *
 * Locked (plan 2026-10-10-001 KTD6, R10) keeps main's editing rule here,
 * silently (R18): the board has no drag, Add, row icons or restore (the shared
 * blocks) — a group keeps only a View icon, which opens its dialog read-only
 * with a single Close (U6: it holds more than the header shows) — and a type
 * opens read-only except Self-Schedule, which still saves
 * at once through the same handler (the type's current add-on with the new
 * value — never a bare `self_schedule`, which would wipe the rest of the
 * add-on). A choice the type can't meet is disabled there: Locked has no
 * Update to hold it for, so nothing is ever left pending and no Required
 * panel shows. The footer is a single Close.
 *
 * The edit modals copy the corp-dashboard ones FORMAT for format (the corp
 * pages are the functional and visual spec): appointment-types-table.tsx
 * (type dialog + nested group dialog) and appointment-type-groups-table.tsx
 * (group + clone dialogs). Same field order, labels, placeholders and hints;
 * the type dialog is 960px with the image + facility on the left; required
 * fields are flagged live (red box, "— required") and listed in a red
 * "Required before saving" panel above the footer, soft suggestions in an
 * amber one; Save is disabled while anything required is missing. Corp's
 * field-styles.ts classes are mirrored as Tailwind strings below (no corp
 * code is imported).
 *
 * Corp-parity behaviour:
 *   - the type form has an image tile (EntityImageTile); a saved type's image
 *     is assigned by the picker at once, a new type's after the create;
 *   - self-scheduling needs an image; Bookable / Members Only also need at
 *     least one resource and one required product (soft suggestions otherwise);
 *   - the Group select has Add / Update buttons opening the group dialog on
 *     top of the type dialog; a group created there is selected at once;
 *   - groups have a Class (attr_id) and, in edit, read-only shortcodes and the
 *     group's appointment types;
 *   - a group name used by a LIVE group warns while typing (the API's 422
 *     DUPLICATE_NAME is the backstop); groups can be cloned with their types;
 *   - the edit forms track changes: a "modified" pill with revert per field,
 *     "Unsaved changes" + Revert in the footer, and a confirm on close.
 */
import { useEffect, useState, useMemo, useCallback, useRef, type ReactNode } from 'react';
import { CalendarCheck, CalendarClock, Lock, ImageIcon, Plus, Trash2, RotateCcw, X, Copy, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { RichDescriptionField } from '@/components/admin/RichTextDialog';
import { descriptionToText } from '@/lib/utils';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { SpinnerOverlay, Spinner } from '@/components/ui/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { CopyButton } from '@/components/ui/CopyButton';
import { CategoryBoard, boardDragHint, type BoardCategory } from '@/components/admin/CategoryBoard';
import { compareTitles, isSeparatorTitle } from '@/components/admin/categoryBoardState';
import { EntityImageTile } from '@/components/admin/EntityImageTile';
import { AdminBoardPage, AdminListEmpty, AdminListToolbar } from '@/components/admin/AdminList';
import { AdminLockedFieldset, AdminLockException } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { FlagIcon, SummaryIcon } from '@/components/admin/CorpBadges';
import { ListFilterBar, anyFilterActive, type ListFilterConfig } from '@/components/admin/ListFilterBar';
import { assignEntityImage } from '@/services/entityImages.service';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import {
  DialogField,
  DialogFooterLayout,
  DialogHeading,
  SwitchField,
  LABEL_CLASS,
  fieldClasses,
  type FieldState,
} from '@/components/admin/DialogChrome';
import { toast } from '@/store/toastStore';
import { appointmentsService } from '@/services/appointments.service';
import type {
  AppointmentTypeDetail,
  AppointmentTypeGroupRow,
  AppointmentTypePayload,
  AppointmentTypeGroupPayload,
  AppointmentTypeGroupAttr,
} from '@/services/appointments.service';
import * as adminSvc from '@/services/admin.service';
import type { Facility } from '@/services/admin.service';
import { getErrorMessage } from '@/services/apiClient';

// ─── Constants ─────────────────────────────────────────────────────────────────

const SELF_SCHEDULE_LABELS: Record<number, string> = {
  0: 'Not Allowed',
  1: 'Bookable',
  2: 'Requestable',
  3: 'Members Only',
};

const REQUIRES_LABELS: Record<number, string> = {
  0: 'Nothing',
  1: 'Patient',
  2: 'Patient & Plan',
};

const CONFLICT_TYPE_OPTIONS = [
  { value: 0, label: 'Same type' },
  { value: 1, label: 'Any appointments' },
];

/** Corp's resource types (appointment-types-table.tsx RESOURCE_TYPES); labels follow the account's role titles. */
const RESOURCE_TYPES = [
  { value: 'doctors', label: 'Doctors' },
  { value: 'nurses', label: 'Nurses' },
  { value: 'coordinators', label: 'Coordinators' },
  { value: 'rooms', label: 'Rooms' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'other', label: 'Other' },
];

/**
 * Modal widths as on corp (the shared Modal's sizes stop at fixed steps): the
 * panel is the wrapper's second child (after the backdrop). md: only, like the
 * Modal's own widths — below md it is the full-width sheet.
 */
const TYPE_DIALOG_WIDTH = 'md:[&>div:nth-child(2)]:max-w-[960px]';
const GROUP_DIALOG_WIDTH = 'md:[&>div:nth-child(2)]:max-w-[560px]';
const CLONE_DIALOG_WIDTH = 'md:[&>div:nth-child(2)]:max-w-[460px]';

// ─── Helpers ───────────────────────────────────────────────────────────────────

function getSelfScheduleIcon(val: number) {
  switch (val) {
    case 1:
      return <span title="Bookable"><CalendarCheck className="h-4 w-4 mx-auto text-orange-500" aria-label="Bookable" /></span>;
    case 2:
      return <span title="Requestable"><CalendarClock className="h-4 w-4 mx-auto text-purple-500" aria-label="Requestable" /></span>;
    case 3:
      return <span title="Members Only"><Lock className="h-4 w-4 mx-auto text-blue-500" aria-label="Members Only" /></span>;
    default:
      return null;
  }
}

/** The type's group, or null for none. Module-level so the board's memo holds. */
function getGroupId(t: AppointmentTypeDetail): number | null {
  return t.group_id ? Number(t.group_id) : null;
}

function getRecordId(t: AppointmentTypeDetail): number {
  return t.appointmentTypeID;
}

function getTypeTitle(t: AppointmentTypeDetail): string {
  return t.name ?? '';
}

/** Soft-deleted type (typeActive 0). Module-level so the board's memo holds. */
function isTypeDeleted(t: AppointmentTypeDetail): boolean {
  return t.active === false;
}

function isGroupDeleted(g: AppointmentTypeGroupRow): boolean {
  return g.active === false || g.deleted === true;
}

/** Both lists, each group once (the later list wins). */
function mergeGroups(...lists: AppointmentTypeGroupRow[][]): AppointmentTypeGroupRow[] {
  const byId = new Map<number, AppointmentTypeGroupRow>();
  for (const list of lists) for (const g of list) byId.set(g.appointmentTypeGroupID, g);
  return [...byId.values()];
}

/** atOrder, then name, then id — rows tied on atOrder (often 0) sort by name. */
function compareTypes(a: AppointmentTypeDetail, b: AppointmentTypeDetail): number {
  return (
    (Number(a.order) || 0) - (Number(b.order) || 0) ||
    (a.name ?? '').localeCompare(b.name ?? '') ||
    a.appointmentTypeID - b.appointmentTypeID
  );
}

/** Alphabetical by name, ignoring case (as the old Appt Types table listed them). */
function compareGroups(a: AppointmentTypeGroupRow, b: AppointmentTypeGroupRow): number {
  return compareTitles(a.name, b.name) || a.appointmentTypeGroupID - b.appointmentTypeGroupID;
}

/**
 * The group-row self-schedule summary: orange when every active type in the
 * group self-schedules, grey when only some do, nothing when none (or none
 * are active).
 */
function GroupSelfScheduleIcon({ types }: { types: AppointmentTypeDetail[] }) {
  const active = types.filter((t) => !isTypeDeleted(t));
  const scheduling = active.filter((t) => (t.addon?.self_schedule ?? 0) > 0).length;
  if (scheduling === 0) return null;
  const all = scheduling === active.length;
  const label = all ? 'Every type self-schedules' : 'Some types self-schedule';
  return (
    <CalendarCheck
      data-testid="group-self-schedule"
      data-all={all || undefined}
      aria-label={label}
      className={`h-4 w-4 mx-auto ${all ? 'text-orange-500' : 'text-slate-300'}`}
    />
  );
}

function toNumberOr(value: string, fallback: number): number {
  const n = Number(value);
  return value.trim() === '' || Number.isNaN(n) ? fallback : n;
}

interface LookupMaps {
  resourceMap: Record<string, string>;
  /** Resource id → raw resource type, where the API sends one. */
  resourceTypeMap?: Record<string, string>;
  productTypeMap: Record<string, string>;
  /** Corp-dashboard filters for the Required Products / Service Credit selects (all types when absent). */
  requiredProductTypeIds?: string[];
  serviceCreditTypeIds?: string[];
  planTypeMap: Record<string, string>;
}

/**
 * A resource type as corp's RESOURCE_TYPES value ('doctor' / 'Doctors' →
 * 'doctors'); '' when there is none, 'other' for anything unknown.
 */
function resourceTypeKey(raw: unknown): string {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === '' || s === 'none') return '';
  if (s === 'equipment' || s === 'other') return s;
  const plural = s.endsWith('s') ? s : `${s}s`;
  return RESOURCE_TYPES.some((t) => t.value === plural) ? plural : 'other';
}

/**
 * A LIVE group already using `name` (trimmed, case-insensitive), other than
 * `excludeId` — the API's DUPLICATE_NAME rule. Deleted groups never count.
 */
function findDuplicateGroup(
  groups: AppointmentTypeGroupRow[],
  name: string,
  excludeId: number | null,
): AppointmentTypeGroupRow | null {
  const needle = name.trim().toLowerCase();
  if (needle === '') return null;
  return (
    groups.find(
      (g) => !isGroupDeleted(g) && g.appointmentTypeGroupID !== excludeId && (g.name ?? '').trim().toLowerCase() === needle,
    ) ?? null
  );
}

function duplicateGroupMessage(g: AppointmentTypeGroupRow): string {
  return `This account already has a group named "${(g.name ?? '').trim()}" (#${g.appointmentTypeGroupID}).`;
}

function errorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/** Field keys whose values differ (arrays compared as JSON). */
function changedKeys<T extends object>(form: T, base: T | null): Set<keyof T> {
  const out = new Set<keyof T>();
  if (!base) return out;
  for (const key of Object.keys(form) as Array<keyof T>) {
    if (JSON.stringify(form[key]) !== JSON.stringify(base[key])) out.add(key);
  }
  return out;
}

// ─── Dialog building blocks (corp's Dialog* equivalents) ───────────────────────

/**
 * One labelled field (DialogChrome's DialogField): label with " *" when
 * required and " — required" when missing, the modified pill on the label's
 * line, the control, and grey help text under it.
 */
function Field({
  id,
  label,
  required = false,
  state = 'none',
  pill,
  hint,
  className,
  children,
}: {
  id?: string;
  label: string;
  required?: boolean;
  state?: FieldState;
  pill?: ReactNode;
  /** Grey help text under the control (corp: text-xs text-gray-500). */
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <DialogField
      htmlFor={id}
      label={label}
      required={required}
      state={state}
      labelHint={required && state === 'error' ? '— required' : undefined}
      badge={pill}
      description={hint}
      className={className}
    >
      {children}
    </DialogField>
  );
}

/**
 * Corp's DynamicRowSection: a bordered list of rows with an Add button and a
 * remove per row. Read-only (a locked FieldLockScope): no Add, no removes.
 */
function DynamicRowSection({
  label,
  addLabel,
  count,
  onAdd,
  emptyMessage,
  children,
}: {
  label: ReactNode;
  addLabel: string;
  count: number;
  onAdd: () => void;
  emptyMessage: string;
  children: ReactNode;
}) {
  const locked = useFieldLocked();
  return (
    <div className="rounded-lg border border-slate-300 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">{label}</div>
        {!locked && (
          <Button variant="secondary" size="sm" onClick={onAdd}>
            {addLabel}
          </Button>
        )}
      </div>
      <div className="mt-2 space-y-2">{count === 0 ? <p className="text-sm text-slate-500">{emptyMessage}</p> : children}</div>
    </div>
  );
}

/** One row of a DynamicRowSection: grey strip, controls, red remove icon. */
function DynamicRow({ removeLabel, onRemove, children }: { removeLabel: string; onRemove: () => void; children: ReactNode }) {
  const locked = useFieldLocked();
  return (
    <div className="overflow-hidden rounded-md border border-slate-300 bg-white">
      <div className="flex items-center gap-2 bg-slate-50 px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
        {!locked && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={removeLabel}
            title="Remove"
            className="h-7 w-7 shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
            leftIcon={<Trash2 className="h-4 w-4" />}
            onClick={onRemove}
          />
        )}
      </div>
    </div>
  );
}

// ─── Change tracking bits ──────────────────────────────────────────────────────

/** The "modified" pill beside a changed field's label, with its revert button. */
function ModifiedPill({ field, onRevert }: { field: string; onRevert: () => void }) {
  return (
    <span
      data-testid="modified-pill"
      className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
    >
      modified
      <button
        type="button"
        className="hover:text-blue-900"
        aria-label={`Revert ${field}`}
        title="Revert"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRevert();
        }}
      >
        <RotateCcw className="h-2.5 w-2.5" aria-hidden />
      </button>
    </span>
  );
}

/** Footer note for an edit form with changes: "Unsaved changes" + Revert (corp). */
function UnsavedNote({ onRevertAll }: { onRevertAll: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex items-center gap-1.5 text-sm text-blue-700">
        <AlertTriangle className="h-4 w-4" aria-hidden />
        Unsaved changes
      </span>
      <Button
        variant="ghost"
        size="xs"
        className="text-slate-500 hover:text-slate-700"
        leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
        onClick={onRevertAll}
      >
        Revert
      </Button>
    </div>
  );
}

function DiscardChangesDialog({ isOpen, onCancel, onDiscard }: { isOpen: boolean; onCancel: () => void; onDiscard: () => void }) {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onCancel}
      onConfirm={onDiscard}
      title="Discard unsaved changes?"
      message="You have unsaved changes. Close without saving them?"
      confirmLabel="Discard changes"
      cancelLabel="Keep editing"
      variant="warning"
    />
  );
}

// ─── Color field ───────────────────────────────────────────────────────────────

const CHECKERBOARD = {
  backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%)',
  backgroundSize: '8px 8px',
};

/**
 * Corp's color field: a swatch (checkerboard when unset — click picks
 * `pickDefault`), the hex text box ("Uses default color"), and a clear X.
 * Read-only (a locked FieldLockScope): the swatch only shows the color, and
 * there is no clear.
 */
function ColorField({
  id,
  label,
  value,
  onChange,
  pickDefault,
  pill,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  pickDefault: string;
  pill?: ReactNode;
}) {
  const locked = useFieldLocked();
  return (
    <Field id={id} label={label} pill={pill}>
      <div className="flex items-center gap-2">
        {locked ? (
          <span
            aria-hidden="true"
            className="h-9 w-12 shrink-0 rounded border border-slate-300 bg-white"
            style={value ? { backgroundColor: value } : CHECKERBOARD}
          />
        ) : value ? (
          <input
            type="color"
            aria-label={`${label} picker`}
            value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#ffffff'}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-12 shrink-0 cursor-pointer rounded border border-slate-300 p-0.5"
          />
        ) : (
          <button
            type="button"
            aria-label={`${label} picker`}
            title="No color set — click to choose"
            onClick={() => onChange(pickDefault)}
            className="h-9 w-12 shrink-0 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
            style={CHECKERBOARD}
          />
        )}
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Uses default color"
          wrapperClassName="min-w-0 flex-1"
        />
        {value && !locked && (
          <button
            type="button"
            className="shrink-0 text-slate-400 hover:text-slate-600"
            onClick={() => onChange('')}
            aria-label={`Clear ${label.toLowerCase()}`}
            title="Clear color"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </Field>
  );
}

// ─── Type modal (add + edit) ───────────────────────────────────────────────────

interface ResourceRow {
  /** A RESOURCE_TYPES value, or '' before one is chosen. */
  type: string;
  /** Resource id, or '' before one is chosen. */
  id: string;
}

interface TypeForm {
  name: string;
  description: string;
  groupId: number | null;
  facilityId: number;
  duration: string;
  patientDuration: string;
  notes: string;
  selfSchedule: number;
  requires: number;
  allowConfirm: boolean;
  color: string;
  fontColor: string;
  firstColor: string;
  firstFontColor: string;
  firstDuration: string;
  firstNotes: string;
  resources: ResourceRow[];
  requiredProducts: Array<{ productTypeId: number | null; quantity: string }>;
  serviceCreditId: number | null;
  planTypeIds: string[];
  maxConflicts: string;
  conflictType: number;
  defaultFee: string;
}

function formFromType(
  t: AppointmentTypeDetail | null,
  defaultGroupId: number | null,
  resourceTypeMap: Record<string, string> = {},
): TypeForm {
  if (!t) {
    return {
      name: '',
      description: '',
      groupId: defaultGroupId,
      facilityId: 0,
      duration: '',
      patientDuration: '',
      notes: '',
      selfSchedule: 0,
      requires: 1,
      allowConfirm: true,
      color: '',
      fontColor: '',
      firstColor: '',
      firstFontColor: '',
      firstDuration: '',
      firstNotes: '',
      resources: [],
      requiredProducts: [],
      serviceCreditId: null,
      planTypeIds: [],
      maxConflicts: '',
      conflictType: 0,
      defaultFee: '',
    };
  }
  const addon = t.addon;
  return {
    name: t.name ?? '',
    description: t.description ?? '',
    groupId: getGroupId(t),
    facilityId: Number(t.facility_id) || 0,
    duration: t.duration ? String(t.duration) : '',
    patientDuration: addon?.patient_duration != null ? String(addon.patient_duration) : '',
    notes: addon?.notes ?? '',
    selfSchedule: Number(addon?.self_schedule) || 0,
    requires: Number(t.requires_patient_plan) || 0,
    allowConfirm: !t.no_confirm,
    color: t.color ?? '',
    fontColor: t.font_color ?? '',
    firstColor: addon?.first_time?.color ?? '',
    firstFontColor: addon?.first_time?.font_color ?? '',
    firstDuration: addon?.first_time?.duration ? String(addon.first_time.duration) : '',
    firstNotes: addon?.first_time?.notes ?? '',
    resources: t.default_resources.map((r) => ({
      type: resourceTypeKey(r.resource_type) || resourceTypeKey(resourceTypeMap[String(r.resource_id)]),
      id: String(r.resource_id),
    })),
    requiredProducts: t.required_products.map((p) => ({
      productTypeId: Number(p.product_type_id) || null,
      quantity: String(p.quantity ?? 1),
    })),
    serviceCreditId: t.service_credit_id ? Number(t.service_credit_id) : null,
    planTypeIds: t.plan_type_ids.map(String),
    maxConflicts: t.max_conflicts != null ? String(t.max_conflicts) : '',
    conflictType: Number(t.conflict_type) || 0,
    defaultFee: t.default_fee != null && t.default_fee !== '' ? String(t.default_fee) : '',
  };
}

interface TypeModalProps {
  /** null = a new type. */
  apptType: AppointmentTypeDetail | null;
  defaultGroupId: number | null;
  /** Live groups (the Group select, and the group dialog's duplicate check). */
  groups: AppointmentTypeGroupRow[];
  facilities: Facility[];
  lookups: LookupMaps;
  /** The type's current image (from the page's image map), or null. */
  imageUrl: string | null;
  nextOrder: (groupId: number) => number;
  /** For the nested group dialog. */
  confirmTypes: Array<{ value: number; label: string }>;
  attrs: AppointmentTypeGroupAttr[];
  nextGroupOrder: number;
  typesOf: (groupId: number) => AppointmentTypeDetail[];
  onClose: () => void;
  onSaved: (saved: AppointmentTypeDetail, isNew: boolean) => void;
  onSelfScheduleChanged: (typeId: number, value: number) => void;
  /** A type's image was assigned (by the picker, or after a create). */
  onImageChanged: (typeId: number, url: string) => void;
  /** A group was added or updated from the Group field's Add / Update buttons. */
  onGroupSaved: (saved: AppointmentTypeGroupRow, isNew: boolean) => void;
}

// Corp's editErrors / formSuggestions sentences.
const NAME_REQUIRED = 'Name is required';
const DURATION_REQUIRED = 'Duration is required';
const GROUP_REQUIRED = 'Group is required';
const IMAGE_REQUIRED = 'Image is required when self-scheduling is enabled';
const RESOURCE_REQUIRED = 'At least one resource is required for bookable/members-only types';
const PRODUCT_REQUIRED = 'At least one product is required for bookable/members-only types';

const TYPE_FIELD_LABELS: Record<keyof TypeForm, string> = {
  name: 'title',
  description: 'description',
  groupId: 'group',
  facilityId: 'facility',
  duration: 'duration',
  patientDuration: 'patient duration',
  notes: 'notes',
  selfSchedule: 'self-schedule',
  requires: 'requires',
  allowConfirm: 'allow confirmations',
  color: 'label color',
  fontColor: 'font color',
  firstColor: 'first label color',
  firstFontColor: 'first font color',
  firstDuration: 'additional time',
  firstNotes: 'first time notes',
  resources: 'resources',
  requiredProducts: 'required products',
  serviceCreditId: 'service credit',
  planTypeIds: 'plan types',
  maxConflicts: 'max appts per slot',
  conflictType: 'apply maximum to',
  defaultFee: 'default schedule fee',
};

/** Bookable (1) or Members Only (3): these need resources and products. */
function isBookableOrMembers(selfSchedule: number): boolean {
  return selfSchedule === 1 || selfSchedule === 3;
}

/** Resource rows with a resource chosen (blank rows are not sent). */
function chosenResources(form: TypeForm) {
  return form.resources.filter((r) => r.id);
}

/** Required-product rows with a product type chosen (blank rows are not sent). */
function chosenProducts(form: TypeForm) {
  return form.requiredProducts.filter((p) => p.productTypeId);
}

interface TypeIssues {
  name: boolean;
  duration: boolean;
  group: boolean;
  image: boolean;
  resources: boolean;
  products: boolean;
}

/** `requireGroup` only on create: an existing type changes group by dragging it on the board. */
function typeIssues(form: TypeForm, imageUrl: string | null, requireGroup = true): TypeIssues {
  const bookable = isBookableOrMembers(form.selfSchedule);
  return {
    name: form.name.trim() === '',
    duration: !(toNumberOr(form.duration, 0) > 0),
    group: requireGroup && !form.groupId,
    image: form.selfSchedule !== 0 && !imageUrl?.trim(),
    resources: bookable && chosenResources(form).length === 0,
    products: bookable && chosenProducts(form).length === 0,
  };
}

/** Nothing flagged (a Locked page shows no required or suggested markers). */
const NO_TYPE_ISSUES: TypeIssues = { name: false, duration: false, group: false, image: false, resources: false, products: false };

/** The self-schedule rules alone (image; resources + products when Bookable / Members Only). */
function breaksSelfScheduleRules(form: TypeForm, imageUrl: string | null): boolean {
  const i = typeIssues(form, imageUrl);
  return i.image || i.resources || i.products;
}

/** Corp's "Required before saving" list, in its order. */
function typeErrorList(i: TypeIssues): string[] {
  const out: string[] = [];
  if (i.name) out.push(NAME_REQUIRED);
  if (i.duration) out.push(DURATION_REQUIRED);
  if (i.group) out.push(GROUP_REQUIRED);
  if (i.image) out.push(IMAGE_REQUIRED);
  if (i.resources) out.push(RESOURCE_REQUIRED);
  if (i.products) out.push(PRODUCT_REQUIRED);
  return out;
}

type GroupDialogState = { kind: 'add' } | { kind: 'edit'; group: AppointmentTypeGroupRow } | null;

function TypeModal({
  apptType,
  defaultGroupId,
  groups,
  facilities,
  lookups,
  imageUrl,
  nextOrder,
  confirmTypes,
  attrs,
  nextGroupOrder,
  typesOf,
  onClose,
  onSaved,
  onSelfScheduleChanged,
  onImageChanged,
  onGroupSaved,
}: TypeModalProps) {
  const isNew = apptType === null;
  // Locked page (KTD6): read-only except Self-Schedule. Only an existing type
  // is ever opened there.
  const locked = useAdminPageLevel() === 'locked';
  const resourceTypeMap = lookups.resourceTypeMap ?? {};
  const [form, setForm] = useState<TypeForm>(() => formFromType(apptType, defaultGroupId, resourceTypeMap));
  // What is saved: the edit form's changes are measured against it. None for a new type.
  const [baseline, setBaseline] = useState<TypeForm | null>(() =>
    apptType ? formFromType(apptType, null, resourceTypeMap) : null,
  );
  const [image, setImage] = useState<string | null>(imageUrl);
  const [saving, setSaving] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [groupDialog, setGroupDialog] = useState<GroupDialogState>(null);
  const { titles } = useRoleTitles();

  const set = <K extends keyof TypeForm>(key: K, value: TypeForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  // Corp flags what is missing live, from the first render — but not on a
  // Locked page, where those fields can't be changed (and Self-Schedule only
  // offers what the type meets).
  const issues = locked ? NO_TYPE_ISSUES : typeIssues(form, image, isNew);
  const errorList = typeErrorList(issues);
  const bookable = isBookableOrMembers(form.selfSchedule);
  const resourceSuggestion = !locked && !bookable && chosenResources(form).length === 0;
  const productSuggestion = !locked && !bookable && chosenProducts(form).length === 0;
  const suggestions = [
    ...(resourceSuggestion ? ['No resources assigned'] : []),
    ...(productSuggestion ? ['No products assigned'] : []),
  ];

  const loadedRequires = apptType ? Number(apptType.requires_patient_plan) || 0 : null;

  // ── Change tracking (edit only).

  const changed = useMemo(() => changedKeys(form, baseline), [form, baseline]);
  const dirty = changed.size > 0;

  function revert(key: keyof TypeForm) {
    if (!baseline) return;
    setForm((f) => ({ ...f, [key]: baseline[key] }));
  }

  function revertAll() {
    if (baseline) setForm(baseline);
  }

  // Locked: Self-Schedule saves itself, so there is never anything to discard.
  function requestClose() {
    if (dirty && !locked) setConfirmDiscard(true);
    else onClose();
  }

  const pill = (key: keyof TypeForm) =>
    changed.has(key) && !locked ? <ModifiedPill field={TYPE_FIELD_LABELS[key]} onRevert={() => revert(key)} /> : null;

  // ── Options.

  const groupOptions = useMemo(() => {
    const opts = groups.map((g) => ({ value: g.appointmentTypeGroupID, label: g.name || `Group #${g.appointmentTypeGroupID}` }));
    if (form.groupId && !opts.some((o) => o.value === form.groupId)) {
      opts.unshift({ value: form.groupId, label: `${apptType?.group_name || 'Deleted group'} (deleted)` });
    }
    return opts;
  }, [groups, form.groupId, apptType?.group_name]);

  const currentGroup = groups.find((g) => g.appointmentTypeGroupID === form.groupId) ?? null;

  // No "All facilities" option: an empty select reads "All Facilities" (corp).
  const facilityOptions = useMemo(() => {
    const opts = facilities.map((f) => ({ value: f.facilityId, label: f.facilityName }));
    if (form.facilityId && !opts.some((o) => o.value === form.facilityId)) {
      opts.push({ value: form.facilityId, label: `Facility #${form.facilityId}` });
    }
    return opts;
  }, [facilities, form.facilityId]);

  // Locked: a choice the type can't meet (no image; no resources or products
  // for Bookable / Members Only) can't be picked — it would wait for an Update
  // the page doesn't have. The same idea as a product's store switch when the
  // product misses the store's requirements.
  const selfScheduleOptions = useMemo(
    () =>
      [0, 1, 2, 3].map((v) => ({
        value: v,
        label: SELF_SCHEDULE_LABELS[v],
        disabled: locked && v !== form.selfSchedule && breaksSelfScheduleRules({ ...form, selfSchedule: v }, image),
      })),
    [locked, form, image],
  );

  // 2 is shown only for a type that already has it — the API refuses it on save.
  const requiresOptions = useMemo(() => {
    const values = loadedRequires === 2 ? [0, 1, 2] : [0, 1];
    return values.map((v) => ({ value: v, label: REQUIRES_LABELS[v] }));
  }, [loadedRequires]);

  const resourceTypeOptions = useMemo(
    () => RESOURCE_TYPES.map((rt) => ({ value: rt.value, label: titles?.[rt.value] ?? rt.label })),
    [titles],
  );

  /** The resources of one type, by name; a resource with no known type is offered under every type. */
  const resourcesOfType = useCallback(
    (type: string, current: string) => {
      const opts = Object.entries(lookups.resourceMap)
        .filter(([id]) => {
          const known = resourceTypeKey(resourceTypeMap[id]);
          return known === '' || known === type;
        })
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
      if (current && !opts.some((o) => o.value === current)) opts.push({ value: current, label: `Resource #${current}` });
      return opts;
    },
    [lookups.resourceMap, resourceTypeMap],
  );

  // Corp-dashboard rule: only live, non-retail product types are offered, and
  // Required Products also need an active product. A value already saved on the
  // type stays listed so it can be seen and removed.
  const productTypeOptionsFor = useCallback(
    (allowed: string[] | undefined, used: (number | null | undefined)[]) => {
      const allow = allowed ? new Set(allowed) : null;
      const opts = Object.entries(lookups.productTypeMap)
        .filter(([value]) => !allow || allow.has(value))
        .map(([value, label]) => ({ value: Number(value), label }))
        .sort((a, b) => a.label.localeCompare(b.label));
      for (const id of used) {
        if (id && !opts.some((o) => o.value === id)) {
          opts.push({ value: id, label: lookups.productTypeMap[String(id)] ?? `Product type #${id}` });
        }
      }
      return opts;
    },
    [lookups.productTypeMap],
  );
  const productTypeOptions = useMemo(
    () => productTypeOptionsFor(lookups.requiredProductTypeIds, form.requiredProducts.map((p) => p.productTypeId)),
    [productTypeOptionsFor, lookups.requiredProductTypeIds, form.requiredProducts],
  );
  const serviceCreditOptions = useMemo(
    () => productTypeOptionsFor(lookups.serviceCreditTypeIds, [form.serviceCreditId]),
    [productTypeOptionsFor, lookups.serviceCreditTypeIds, form.serviceCreditId],
  );

  const planTypeOptions = useMemo(() => {
    const opts = Object.entries(lookups.planTypeMap)
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    for (const id of form.planTypeIds) {
      if (!opts.some((o) => o.value === id)) opts.push({ value: id, label: `Plan type #${id}` });
    }
    return opts;
  }, [lookups.planTypeMap, form.planTypeIds]);

  // ── Image.

  function handleImageSelected(url: string) {
    setImage(url);
    // A saved type's image is already assigned by the picker.
    if (apptType) onImageChanged(apptType.appointmentTypeID, url);
  }

  // ── Group (nested dialog).

  function handleGroupSaved(saved: AppointmentTypeGroupRow, isGroupNew: boolean) {
    onGroupSaved(saved, isGroupNew);
    // Corp: a group created from here becomes the type's group.
    if (isGroupNew && saved && typeof saved.appointmentTypeGroupID === 'number') set('groupId', saved.appointmentTypeGroupID);
    setGroupDialog(null);
  }

  // ── Saves.

  /**
   * Existing types: self-schedule saves at once, as before — unless the new
   * value breaks a self-schedule rule. Then it stays a pending change (the
   * required panel says why) and goes out with "Update" once the form is fixed.
   * A Locked page has no Update, so there such a value is never taken (its
   * choice is disabled; this is the backstop).
   */
  async function handleSelfScheduleChange(value: number) {
    if (!apptType || breaksSelfScheduleRules({ ...form, selfSchedule: value }, image)) {
      if (!locked) set('selfSchedule', value);
      return;
    }
    const prev = form.selfSchedule;
    set('selfSchedule', value);
    setSavingSchedule(true);
    try {
      await appointmentsService.updateAppointmentTypeSelfSchedule(apptType.appointmentTypeID, value, apptType.addon);
      setBaseline((b) => (b ? { ...b, selfSchedule: value } : b));
      onSelfScheduleChanged(apptType.appointmentTypeID, value);
      toast.success(`Self-schedule updated to ${SELF_SCHEDULE_LABELS[value]}`);
    } catch (err) {
      set('selfSchedule', prev);
      toast.error(getErrorMessage(err, 'Failed to update self-schedule'));
    } finally {
      setSavingSchedule(false);
    }
  }

  async function handleSave() {
    if (typeErrorList(typeIssues(form, image, isNew)).length > 0) return;
    const groupId = form.groupId as number;

    const payload: AppointmentTypePayload = {
      name: form.name.trim(),
      description: form.description,
      duration: toNumberOr(form.duration, 0),
      color: form.color,
      font_color: form.fontColor,
      group_id: groupId,
      facility_id: form.facilityId,
      max_conflicts: toNumberOr(form.maxConflicts, 0),
      conflict_type: form.conflictType,
      no_confirm: !form.allowConfirm,
      default_fee: form.defaultFee.trim() === '' ? null : toNumberOr(form.defaultFee, 0),
      addon: {
        patient_duration: toNumberOr(form.patientDuration, 0),
        notes: form.notes,
        self_schedule: form.selfSchedule,
        first_time: {
          notes: form.firstNotes,
          duration: toNumberOr(form.firstDuration, 0),
          color: form.firstColor,
          font_color: form.firstFontColor,
        },
      },
      resource_ids: chosenResources(form).map((r) => Number(r.id)).filter(Boolean),
      required_products: chosenProducts(form).map((p) => ({
        product_type_id: p.productTypeId as number,
        quantity: Math.max(1, toNumberOr(p.quantity, 1)),
      })),
      service_credit_id: form.serviceCreditId || null,
      plan_type_ids: form.planTypeIds.map(Number).filter(Boolean),
    };
    // Unchanged requirement is left out: the API keeps it, and refuses 2.
    if (isNew || form.requires !== loadedRequires) payload.requires_patient_plan = form.requires;
    // New, or moved with the group select: lands last in the group.
    if (isNew) {
      payload.order = nextOrder(groupId);
      payload.active = true;
    } else if (groupId !== getGroupId(apptType)) {
      payload.order = nextOrder(groupId);
    }

    setSaving(true);
    try {
      const saved = apptType
        ? await appointmentsService.updateAppointmentType(apptType.appointmentTypeID, payload)
        : await appointmentsService.createAppointmentType(payload);
      // A new type's chosen image is assigned once the type exists.
      const newId = isNew ? saved?.appointmentTypeID : undefined;
      if (isNew && image && typeof newId === 'number') {
        try {
          await assignEntityImage('appointment-type', newId, image);
          onImageChanged(newId, image);
        } catch (err) {
          toast.error(getErrorMessage(err, 'The appointment type was added, but its image could not be saved.'));
        }
      }
      onSaved(saved, isNew);
      toast.success(isNew ? 'Appointment type added' : 'Appointment type saved');
    } catch (err) {
      toast.error(getErrorMessage(err, isNew ? 'The appointment type could not be added.' : 'The appointment type could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  // ── Row editors.

  const setResource = (idx: number, row: ResourceRow) =>
    set('resources', form.resources.map((r, i) => (i === idx ? row : r)));
  const setProduct = (idx: number, row: TypeForm['requiredProducts'][number]) =>
    set('requiredProducts', form.requiredProducts.map((p, i) => (i === idx ? row : p)));

  const resourceState: FieldState = issues.resources ? 'error' : resourceSuggestion ? 'warning' : 'none';
  const productState: FieldState = issues.products ? 'error' : productSuggestion ? 'warning' : 'none';

  // Locked: a viewer's footer (Self-Schedule saves itself) — no Update, Cancel,
  // Revert or Required / Suggestions panels.
  const footer = locked ? (
    <DialogFooterLayout>
      <div className="flex-1" />
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </DialogFooterLayout>
  ) : (
    <DialogFooterLayout errors={errorList} suggestions={suggestions} isCreating={isNew}>
      {dirty && <UnsavedNote onRevertAll={revertAll} />}
      <div className="ml-auto flex gap-2">
        <Button variant="secondary" onClick={requestClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => void handleSave()} loading={saving} disabled={errorList.length > 0}>
          {isNew ? 'Create' : 'Update'}
        </Button>
      </div>
    </DialogFooterLayout>
  );

  const body = (
    <>
      <div className="flex flex-col gap-6 md:flex-row">
        {/* Left column: image + facility */}
        <div className="space-y-4 md:w-48 md:shrink-0">
          <EntityImageTile
            entityType="appointment-type"
            entityId={apptType?.appointmentTypeID ?? null}
            imageUrl={image}
            onSelected={handleImageSelected}
            folder="appointments"
            label="Image"
            required={form.selfSchedule !== 0}
          />
          <Field id="type-facility" label="Facility" pill={pill('facilityId')}>
            <Select<number>
              id="type-facility"
              placeholder="All Facilities"
              searchable
              clearable
              options={facilityOptions}
              value={form.facilityId || null}
              onChange={(v) => set('facilityId', v ?? 0)}
            />
          </Field>
        </div>

        {/* Right column: fields, in corp's order */}
        <div className="min-w-0 flex-1 space-y-4">
          <Field id="type-name" label="Title" required state={issues.name ? 'error' : 'none'} pill={pill('name')}>
            <Input
              id="type-name"
              placeholder="Appointment type name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className={fieldClasses.input(issues.name ? 'error' : 'none')}
            />
          </Field>

          <Field id="type-description" label="Description" pill={pill('description')}>
            <RichDescriptionField
              id="type-description"
              value={form.description}
              onChange={(html) => set('description', html)}
              dialogTitle="Edit Appointment Type Description"
            />
          </Field>

          {/* New types only — an existing type changes group by dragging it on the board. */}
          {isNew && (
          <Field id="type-group" label="Group" required state={issues.group ? 'error' : 'none'} pill={pill('groupId')}>
            <div className="flex items-center gap-2">
              <Select<number>
                id="type-group"
                wrapperClassName="min-w-0 flex-1"
                className={fieldClasses.input(issues.group ? 'error' : 'none')}
                placeholder="Select group"
                searchable
                options={groupOptions}
                value={form.groupId}
                onChange={(v) => set('groupId', v)}
              />
              <Button variant="secondary" size="sm" aria-label="Add group" title="Add a new group" onClick={() => setGroupDialog({ kind: 'add' })}>
                Add
              </Button>
              <Button
                variant="secondary"
                size="sm"
                aria-label="Update group"
                title="Edit the selected group"
                disabled={!currentGroup}
                onClick={() => currentGroup && setGroupDialog({ kind: 'edit', group: currentGroup })}
              >
                Update
              </Button>
            </div>
          </Field>
          )}

          <Field id="type-notes" label="Notes" pill={pill('notes')}>
            <Textarea
              id="type-notes"
              rows={3}
              placeholder="Internal notes"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="type-duration" label="Duration (min)" required state={issues.duration ? 'error' : 'none'} pill={pill('duration')}>
              <Input
                id="type-duration"
                type="number"
                min={1}
                placeholder="30"
                value={form.duration}
                onChange={(e) => set('duration', e.target.value)}
                className={fieldClasses.input(issues.duration ? 'error' : 'none')}
              />
            </Field>
            <Field id="type-patient-duration" label="Patient Duration (min)" pill={pill('patientDuration')}>
              <Input
                id="type-patient-duration"
                type="number"
                min={0}
                placeholder="Same as duration"
                value={form.patientDuration}
                onChange={(e) => set('patientDuration', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* The Locked exception: on a Locked page this still changes, and saves at once. */}
            <AdminLockException>
              <Field id="type-self-schedule" label="Self-Schedule" pill={pill('selfSchedule')}>
                <Select<number>
                  id="type-self-schedule"
                  options={selfScheduleOptions}
                  value={form.selfSchedule}
                  onChange={(v) => void handleSelfScheduleChange(v ?? 0)}
                  disabled={savingSchedule}
                />
              </Field>
            </AdminLockException>
            <Field id="type-requires" label="Requires" pill={pill('requires')}>
              <Select<number>
                id="type-requires"
                options={requiresOptions}
                value={form.requires}
                onChange={(v) => set('requires', v ?? 0)}
              />
            </Field>
            <div className="grid gap-2">
              <span className={LABEL_CLASS} aria-hidden="true">
                &nbsp;
              </span>
              <SwitchField
                id="type-allow-confirm"
                className="flex h-9 items-center"
                label="Allow Confirmations"
                ariaLabel="Allow Confirmations"
                checked={form.allowConfirm}
                onChange={(v) => set('allowConfirm', v)}
                badge={pill('allowConfirm')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ColorField
              id="type-color"
              label="Label Color"
              value={form.color}
              onChange={(v) => set('color', v)}
              pickDefault="#000000"
              pill={pill('color')}
            />
            <ColorField
              id="type-font-color"
              label="Font Color"
              value={form.fontColor}
              onChange={(v) => set('fontColor', v)}
              pickDefault="#ffffff"
              pill={pill('fontColor')}
            />
          </div>

          {/* First Time Details */}
          <section aria-label="First Time Details" className="mt-2 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
            <h4 className="mb-3 text-sm font-semibold text-slate-700">First Time Details</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ColorField
                id="type-first-color"
                label="First Label Color"
                value={form.firstColor}
                onChange={(v) => set('firstColor', v)}
                pickDefault="#000000"
                pill={pill('firstColor')}
              />
              <ColorField
                id="type-first-font-color"
                label="First Font Color"
                value={form.firstFontColor}
                onChange={(v) => set('firstFontColor', v)}
                pickDefault="#ffffff"
                pill={pill('firstFontColor')}
              />
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field id="type-first-duration" label="Additional Time (min)" pill={pill('firstDuration')}>
                <Input
                  id="type-first-duration"
                  type="number"
                  min={0}
                  placeholder="0"
                  value={form.firstDuration}
                  onChange={(e) => set('firstDuration', e.target.value)}
                />
              </Field>
            </div>
            <Field id="type-first-notes" label="First Time Notes" pill={pill('firstNotes')} className="mt-4">
              <Textarea
                id="type-first-notes"
                rows={3}
                placeholder="Notes for first-time appointments"
                value={form.firstNotes}
                onChange={(e) => set('firstNotes', e.target.value)}
              />
            </Field>
          </section>

          {/* People / Resources */}
          <div data-testid="resources-field" className={fieldClasses.wrapper(resourceState)}>
            {resourceState === 'error' && <p className={fieldClasses.message('error')}>Required for bookable/members-only types</p>}
            {resourceState === 'warning' && <p className={fieldClasses.message('warning')}>Suggestion: Assign resources</p>}
            <DynamicRowSection
              label={
                <>
                  <span className={LABEL_CLASS}>
                    People / Resources
                    {bookable && ' *'}
                  </span>
                  {pill('resources')}
                </>
              }
              addLabel="Add resource"
              count={form.resources.length}
              onAdd={() => set('resources', [...form.resources, { type: '', id: '' }])}
              emptyMessage="No resources added."
            >
              {form.resources.map((row, idx) => (
                <DynamicRow
                  key={idx}
                  removeLabel="Remove resource"
                  onRemove={() => set('resources', form.resources.filter((_, i) => i !== idx))}
                >
                  <Select<string>
                    id={`type-resource-type-${idx}`}
                    wrapperClassName="w-[140px] shrink-0"
                    placeholder="Select type"
                    options={resourceTypeOptions}
                    value={row.type || null}
                    onChange={(v) => setResource(idx, { type: v ?? '', id: '' })}
                  />
                  <Select<string>
                    id={`type-resource-${idx}`}
                    wrapperClassName="min-w-0 flex-1"
                    placeholder={row.type ? 'Select resource' : 'Select a type first'}
                    searchable
                    disabled={!row.type}
                    options={row.type ? resourcesOfType(row.type, row.id) : []}
                    value={row.id || null}
                    onChange={(v) => setResource(idx, { ...row, id: v ?? '' })}
                  />
                </DynamicRow>
              ))}
            </DynamicRowSection>
          </div>

          {/* Required Products */}
          <div data-testid="products-field" className={fieldClasses.wrapper(productState)}>
            {productState === 'error' && <p className={fieldClasses.message('error')}>Required for bookable/members-only types</p>}
            {productState === 'warning' && <p className={fieldClasses.message('warning')}>Suggestion: Assign products</p>}
            <DynamicRowSection
              label={
                <>
                  <span className={LABEL_CLASS}>
                    Required Products
                    {bookable && ' *'}
                  </span>
                  {pill('requiredProducts')}
                </>
              }
              addLabel="Add product"
              count={form.requiredProducts.length}
              onAdd={() => set('requiredProducts', [...form.requiredProducts, { productTypeId: null, quantity: '1' }])}
              emptyMessage="No required products."
            >
              {form.requiredProducts.map((row, idx) => (
                <DynamicRow
                  key={idx}
                  removeLabel="Remove product"
                  onRemove={() => set('requiredProducts', form.requiredProducts.filter((_, i) => i !== idx))}
                >
                  <Select<number>
                    id={`type-product-${idx}`}
                    wrapperClassName="min-w-0 flex-1"
                    placeholder="Select product type"
                    searchable
                    options={productTypeOptions}
                    value={row.productTypeId}
                    onChange={(v) => setProduct(idx, { ...row, productTypeId: v })}
                  />
                  <Input
                    aria-label="Quantity"
                    type="number"
                    min={1}
                    placeholder="Qty"
                    wrapperClassName="w-20 shrink-0"
                    fullWidth={false}
                    value={row.quantity}
                    onChange={(e) => setProduct(idx, { ...row, quantity: e.target.value })}
                  />
                </DynamicRow>
              ))}
            </DynamicRowSection>
          </div>

          {/* Service Credit */}
          <div className="space-y-2 rounded-md border border-slate-200 p-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <label htmlFor="type-service-credit" className={LABEL_CLASS}>
                Service Credit
              </label>
              {pill('serviceCreditId')}
            </div>
            <div className="flex items-center gap-2">
              <Select<number>
                id="type-service-credit"
                wrapperClassName="min-w-0 flex-1"
                placeholder="Select product type"
                searchable
                options={serviceCreditOptions}
                value={form.serviceCreditId}
                onChange={(v) => set('serviceCreditId', v)}
              />
              {form.serviceCreditId && !locked && (
                <button
                  type="button"
                  className="shrink-0 text-slate-400 hover:text-red-600"
                  onClick={() => set('serviceCreditId', null)}
                  aria-label="Clear service credit"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Plan Types */}
          <div className="space-y-2 rounded-md border border-slate-200 p-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <label htmlFor="type-plan-types" className={LABEL_CLASS}>
                Plan Types
              </label>
              {pill('planTypeIds')}
            </div>
            {planTypeOptions.length === 0 ? (
              <p className="py-2 text-sm text-slate-500">No plan types available.</p>
            ) : (
              <>
                <SearchableMultiSelect
                  id="type-plan-types"
                  options={planTypeOptions}
                  selectedValues={form.planTypeIds}
                  onSelectionChange={(values) => set('planTypeIds', values)}
                  placeholder="No plan types"
                  searchPlaceholder="Search plan types…"
                />
                {form.planTypeIds.length > 0 && (
                  <div data-testid="plan-type-chips" className="space-y-1">
                    {form.planTypeIds.map((id) => {
                      const label = planTypeOptions.find((o) => o.value === id)?.label ?? `Plan type #${id}`;
                      return (
                        <div key={id} className="flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs">
                          <span className="flex-1 truncate">{label}</span>
                          {!locked && (
                            <button
                              type="button"
                              className="shrink-0 text-slate-400 hover:text-red-600"
                              aria-label={`Remove plan type ${label}`}
                              onClick={() => set('planTypeIds', form.planTypeIds.filter((p) => p !== id))}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field id="type-max-conflicts" label="Max Appts Per Slot" pill={pill('maxConflicts')}>
              <Input
                id="type-max-conflicts"
                type="number"
                min={0}
                placeholder="Unlimited"
                value={form.maxConflicts}
                onChange={(e) => set('maxConflicts', e.target.value)}
              />
            </Field>
            <Field id="type-conflict-type" label="Apply Maximum To" pill={pill('conflictType')}>
              <Select<number>
                id="type-conflict-type"
                options={CONFLICT_TYPE_OPTIONS}
                value={form.conflictType}
                onChange={(v) => set('conflictType', v ?? 0)}
              />
            </Field>
            <Field id="type-default-fee" label="Default Schedule Fee" pill={pill('defaultFee')}>
              <Input
                id="type-default-fee"
                type="number"
                step="0.01"
                min={0}
                placeholder="None"
                value={form.defaultFee}
                onChange={(e) => set('defaultFee', e.target.value)}
              />
            </Field>
          </div>
        </div>
      </div>

      {groupDialog && (
        <GroupModal
          key={groupDialog.kind === 'edit' ? `edit-${groupDialog.group.appointmentTypeGroupID}` : 'add'}
          group={groupDialog.kind === 'edit' ? groupDialog.group : null}
          facilities={facilities}
          confirmTypes={confirmTypes}
          attrs={attrs}
          liveGroups={groups}
          nextOrder={nextGroupOrder}
          typesOf={typesOf}
          onClose={() => setGroupDialog(null)}
          onSaved={handleGroupSaved}
        />
      )}
      <DiscardChangesDialog isOpen={confirmDiscard} onCancel={() => setConfirmDiscard(false)} onDiscard={onClose} />
    </>
  );

  return (
    // Escape never closes this long form: the image picker, the group dialog
    // and the prompts open on top of it, and Escape there must not close this
    // too (the shared Modal listens on the document, so every open modal hears it).
    <Modal
      isOpen
      onClose={requestClose}
      title={
        <DialogHeading
          variant={isNew || locked ? 'default' : 'edit'}
          title={isNew ? 'Add Appointment Type' : locked ? 'Appointment Type' : 'Edit Appointment Type'}
          description={isNew ? 'Create a new appointment type.' : 'Update the appointment type details.'}
        />
      }
      size="xl"
      wrapperClassName={TYPE_DIALOG_WIDTH}
      closeOnEscape={false}
      footer={footer}
    >
      {/* Locked: every field is read-only except Self-Schedule (its AdminLockException). */}
      <AdminLockedFieldset>{body}</AdminLockedFieldset>
    </Modal>
  );
}

// ─── Group modal (add + edit) ──────────────────────────────────────────────────

interface GroupForm {
  name: string;
  description: string;
  color: string;
  facilityId: number;
  confirmTypeId: number;
  /** Class (appointmentTypeGroupAttrID); 0 = none. */
  attrId: number;
}

const EMPTY_GROUP_FORM: GroupForm = { name: '', description: '', color: '', facilityId: 0, confirmTypeId: 0, attrId: 0 };

const GROUP_FIELD_LABELS: Record<keyof GroupForm, string> = {
  name: 'title',
  description: 'description',
  color: 'background color',
  facilityId: 'facility',
  confirmTypeId: 'confirmation',
  attrId: 'class',
};

function groupFormFrom(g: AppointmentTypeGroupRow): GroupForm {
  return {
    name: g.name ?? '',
    description: g.description ?? '',
    color: g.background_color ?? '',
    facilityId: Number(g.group_facility_id) || 0,
    confirmTypeId: Number(g.confirm_type_id) || 0,
    attrId: Number(g.attr_id) || 0,
  };
}

/** "None" first; an option named "none" (or blank) is dropped, as on corp. */
function classOptions(attrs: AppointmentTypeGroupAttr[], current: number) {
  const opts = [
    { value: 0, label: 'None' },
    ...attrs
      .filter((a) => (a.name ?? '').trim() !== '' && a.name.trim().toLowerCase() !== 'none')
      .map((a) => ({ value: Number(a.appointmentTypeGroupAttrID), label: a.name.trim() })),
  ];
  if (current && !opts.some((o) => o.value === current)) opts.push({ value: current, label: `Class #${current}` });
  return opts;
}

interface GroupModalProps {
  /** null = a new group. */
  group: AppointmentTypeGroupRow | null;
  facilities: Facility[];
  confirmTypes: Array<{ value: number; label: string }>;
  attrs: AppointmentTypeGroupAttr[];
  /** Live groups, for the duplicate-name warning. */
  liveGroups: AppointmentTypeGroupRow[];
  nextOrder: number;
  /** The active types filed under a group (the read-only list in edit). */
  typesOf: (groupId: number) => AppointmentTypeDetail[];
  onClose: () => void;
  onSaved: (saved: AppointmentTypeGroupRow, isNew: boolean) => void;
}

function GroupModal({ group, facilities, confirmTypes, attrs, liveGroups, nextOrder, typesOf, onClose, onSaved }: GroupModalProps) {
  const isNew = group === null;
  // Locked page (U6): a viewer, opened from the group's View icon. The fields
  // show their values, nothing saves, and the footer is a single Close (R18).
  const locked = useAdminPageLevel() === 'locked';
  // Editing reads the group first: the list has no stage_id, and an update
  // without one resets the stage.
  const [loaded, setLoaded] = useState<AppointmentTypeGroupRow | null>(group ? null : ({} as AppointmentTypeGroupRow));
  const [form, setForm] = useState<GroupForm>(EMPTY_GROUP_FORM);
  const [baseline, setBaseline] = useState<GroupForm | null>(null);
  // The API's refusal (422, e.g. DUPLICATE_NAME).
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [typesExpanded, setTypesExpanded] = useState(false);

  useEffect(() => {
    if (!group) return;
    let cancelled = false;
    appointmentsService
      .getAppointmentTypeGroup(group.appointmentTypeGroupID)
      .then((fresh) => {
        if (cancelled) return;
        const g = { ...group, ...fresh };
        const f = groupFormFrom(g);
        setLoaded(g);
        setForm(f);
        setBaseline(f);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(getErrorMessage(err, 'The group could not be loaded.'));
        onClose();
      });
    return () => {
      cancelled = true;
    };
    // Load once per opened group.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.appointmentTypeGroupID]);

  const set = <K extends keyof GroupForm>(key: K, value: GroupForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const changed = useMemo(() => changedKeys(form, baseline), [form, baseline]);
  const dirty = changed.size > 0;

  function revert(key: keyof GroupForm) {
    if (!baseline) return;
    setForm((f) => ({ ...f, [key]: baseline[key] }));
    if (key === 'name') setError(undefined);
  }

  function revertAll() {
    if (!baseline) return;
    setForm(baseline);
    setError(undefined);
  }

  function requestClose() {
    if (dirty && !locked) setConfirmDiscard(true);
    else onClose();
  }

  const pill = (key: keyof GroupForm) =>
    changed.has(key) && !locked ? <ModifiedPill field={GROUP_FIELD_LABELS[key]} onRevert={() => revert(key)} /> : null;

  const duplicate = useMemo(
    () => findDuplicateGroup(liveGroups, form.name, group?.appointmentTypeGroupID ?? null),
    [liveGroups, form.name, group?.appointmentTypeGroupID],
  );
  // Locked: nothing can change, so nothing is flagged.
  const nameError = locked ? undefined : (error ?? (duplicate ? duplicateGroupMessage(duplicate) : undefined));
  const nameMissing = !locked && loaded !== null && form.name.trim() === '';
  const groupErrors = nameMissing ? ['Title is required'] : [];

  const facilityOptions = useMemo(() => {
    const opts = [{ value: 0, label: 'None' }, ...facilities.map((f) => ({ value: f.facilityId, label: f.facilityName }))];
    if (form.facilityId && !opts.some((o) => o.value === form.facilityId)) {
      opts.push({ value: form.facilityId, label: `Facility #${form.facilityId}` });
    }
    return opts;
  }, [facilities, form.facilityId]);

  const confirmOptions = useMemo(() => {
    const opts = [{ value: 0, label: 'None' }, ...confirmTypes];
    if (form.confirmTypeId && !opts.some((o) => o.value === form.confirmTypeId)) {
      opts.push({ value: form.confirmTypeId, label: `Confirmation #${form.confirmTypeId}` });
    }
    return opts;
  }, [confirmTypes, form.confirmTypeId]);

  const attrOptions = useMemo(() => classOptions(attrs, form.attrId), [attrs, form.attrId]);

  async function handleSave() {
    // Locked: a viewer never saves (Enter in a field would otherwise submit).
    if (locked) return;
    const trimmed = form.name.trim();
    if (trimmed === '') return;
    // The warning is already on the field; the API would refuse it anyway.
    if (duplicate) return;
    const payload: AppointmentTypeGroupPayload = {
      name: trimmed,
      description: form.description,
      background_color: form.color,
      group_facility_id: form.facilityId,
      confirm_type_id: form.confirmTypeId,
      attr_id: form.attrId,
    };
    if (isNew) {
      payload.order = nextOrder;
      payload.active = true;
    } else if (loaded?.stage_id) {
      payload.stage_id = loaded.stage_id;
    }
    setSaving(true);
    try {
      const saved = group
        ? await appointmentsService.updateAppointmentTypeGroup(group.appointmentTypeGroupID, payload)
        : await appointmentsService.createAppointmentTypeGroup(payload);
      onSaved(saved, isNew);
    } catch (err) {
      const message = getErrorMessage(err, 'The group could not be saved.');
      if (errorStatus(err) === 422) setError(message);
      else toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  const groupId = group?.appointmentTypeGroupID;
  const shortcodes = groupId
    ? [
        { label: 'Date', code: `\${AppointmentGroup${groupId}Date}` },
        { label: 'Time', code: `\${AppointmentGroup${groupId}Time}` },
        { label: 'Date & Time', code: `\${AppointmentGroup${groupId}DateTime}` },
      ]
    : [];
  const groupTypes = groupId ? typesOf(groupId) : [];

  return (
    <Modal
      isOpen
      onClose={requestClose}
      title={
        <DialogHeading
          variant={isNew || locked ? 'default' : 'edit'}
          title={isNew ? 'Add Group' : locked ? 'Group' : 'Edit Group'}
          description={isNew ? 'Create a new group for organizing appointment types.' : 'Update the appointment type group.'}
        />
      }
      size="md"
      wrapperClassName={GROUP_DIALOG_WIDTH}
      // While the discard prompt is open, Escape belongs to it.
      closeOnEscape={!confirmDiscard}
      footer={
        locked ? (
          <DialogFooterLayout>
            <div className="flex-1" />
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </DialogFooterLayout>
        ) : (
          <DialogFooterLayout errors={groupErrors} isCreating={isNew}>
            {dirty && <UnsavedNote onRevertAll={revertAll} />}
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" onClick={requestClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void handleSave()} loading={saving} disabled={loaded === null || groupErrors.length > 0}>
                {isNew ? 'Create' : 'Update'}
              </Button>
            </div>
          </DialogFooterLayout>
        )
      }
    >
      {loaded === null ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <AdminLockedFieldset>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSave();
            }}
          >
            <Field id="group-name" label="Title" required state={nameMissing ? 'error' : 'none'} pill={pill('name')}>
              <Input
                id="group-name"
                autoFocus
                placeholder="Group title"
                value={form.name}
                onChange={(e) => {
                  set('name', e.target.value);
                  setError(undefined);
                }}
                error={nameError}
                className={fieldClasses.input(nameMissing ? 'error' : 'none')}
              />
            </Field>
            <Field id="group-class" label="Class" pill={pill('attrId')}>
              <Select<number>
                id="group-class"
                placeholder="Select class"
                options={attrOptions}
                value={form.attrId}
                onChange={(v) => set('attrId', v ?? 0)}
              />
            </Field>
            <Field id="group-facility" label="Facility" pill={pill('facilityId')}>
              <Select<number>
                id="group-facility"
                placeholder="Select facility"
                options={facilityOptions}
                value={form.facilityId}
                onChange={(v) => set('facilityId', v ?? 0)}
              />
            </Field>
            <Field id="group-confirmation" label="Confirmation" pill={pill('confirmTypeId')}>
              <Select<number>
                id="group-confirmation"
                placeholder="Select confirmation type"
                options={confirmOptions}
                value={form.confirmTypeId}
                onChange={(v) => set('confirmTypeId', v ?? 0)}
              />
            </Field>
            {/* Not on corp's group dialog: Prospera's API fields, kept after corp's own. */}
            <Field id="group-description" label="Description" pill={pill('description')}>
              <Textarea autoResize
                id="group-description"
                rows={2}
                placeholder="Optional description"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </Field>
            <ColorField
              id="group-color"
              label="Background Color"
              value={form.color}
              onChange={(v) => set('color', v)}
              pickDefault="#ffffff"
              pill={pill('color')}
            />

            {shortcodes.length > 0 && (
              <section aria-label="Shortcodes" className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                <h4 className="mb-2 text-sm font-semibold text-slate-700">Shortcodes</h4>
                <div className="grid gap-1.5 text-sm text-slate-600">
                  {shortcodes.map((s) => (
                    <div key={s.label} className="flex items-center gap-2">
                      <span className="w-28 shrink-0 text-slate-500">{s.label}:</span>
                      <code className="min-w-0 flex-1 truncate rounded border border-slate-200 bg-white px-2 py-0.5 font-mono text-xs">{s.code}</code>
                      <CopyButton value={s.code} label={`${s.label} shortcode`} size="sm" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {groupId !== undefined && (
              <div className="space-y-2 border-t border-slate-200 pt-2">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 text-left"
                  aria-expanded={typesExpanded}
                  onClick={() => setTypesExpanded((v) => !v)}
                >
                  {typesExpanded ? (
                    <ChevronDown className="h-4 w-4 text-slate-500" aria-hidden />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-500" aria-hidden />
                  )}
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                    <CalendarCheck className="h-3.5 w-3.5 text-blue-600" aria-hidden />
                    Appointment Types
                    <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0 text-[10px] font-semibold text-blue-700">
                      {groupTypes.length}
                    </span>
                  </span>
                </button>
                {typesExpanded && (
                  <div data-testid="group-types" className="ml-6 space-y-1">
                    {groupTypes.length === 0 ? (
                      <p className="text-xs italic text-slate-400">No appointment types in this group</p>
                    ) : (
                      groupTypes.map((t) => (
                        <div
                          key={t.appointmentTypeID}
                          className="flex items-center gap-2 rounded border border-blue-100 bg-blue-50/50 px-2 py-1 text-xs"
                        >
                          <CalendarCheck className="h-3 w-3 shrink-0 text-blue-500" aria-hidden />
                          <span className="flex-1 truncate">{t.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
        </AdminLockedFieldset>
      )}
      <DiscardChangesDialog isOpen={confirmDiscard} onCancel={() => setConfirmDiscard(false)} onDiscard={onClose} />
    </Modal>
  );
}

// ─── Clone group modal ─────────────────────────────────────────────────────────

interface CloneGroupModalProps {
  source: AppointmentTypeGroupRow;
  /** Active types in the source group (only those are copied). */
  typeCount: number;
  facilities: Facility[];
  liveGroups: AppointmentTypeGroupRow[];
  onClose: () => void;
  onCloned: (created: AppointmentTypeGroupRow | null) => void;
}

function CloneGroupModal({ source, typeCount, facilities, liveGroups, onClose, onCloned }: CloneGroupModalProps) {
  const [name, setName] = useState(`Copy of ${source.name ?? ''}`);
  const [facilityId, setFacilityId] = useState(0);
  const [error, setError] = useState<string | undefined>();
  const [cloning, setCloning] = useState(false);

  const duplicate = useMemo(() => findDuplicateGroup(liveGroups, name, null), [liveGroups, name]);
  const nameError = error ?? (duplicate ? duplicateGroupMessage(duplicate) : undefined);
  const nameMissing = name.trim() === '';

  const facilityOptions = useMemo(
    () => [
      { value: 0, label: 'Keep original facility' },
      ...facilities.filter((f) => f.active !== false).map((f) => ({ value: f.facilityId, label: f.facilityName })),
    ],
    [facilities],
  );

  async function handleClone() {
    const trimmed = name.trim();
    if (trimmed === '' || duplicate) return;
    setCloning(true);
    try {
      const created = await appointmentsService.cloneAppointmentTypeGroup(source.appointmentTypeGroupID, {
        name: trimmed,
        facility_id: facilityId || null,
      });
      const count = created && typeof created.appointment_types_count === 'number' ? created.appointment_types_count : typeCount;
      toast.success(`"${source.name}" and its ${count} appointment type(s) were duplicated`);
      onCloned(created ?? null);
    } catch (err) {
      const message = getErrorMessage(err, 'The group could not be cloned.');
      if (errorStatus(err) === 422) setError(message);
      else toast.error(message);
    } finally {
      setCloning(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={cloning ? () => {} : onClose}
      title={
        <DialogHeading
          variant="clone"
          title="Clone Appointment Type Group"
          description={
            <>
              This creates a duplicate of <strong>{source.name}</strong> in the same account, along with all {typeCount} of its
              appointment type(s) and their settings (products, add-ons, plan associations, service credits, default resources,
              current fee, and images).
            </>
          }
        />
      }
      size="md"
      wrapperClassName={CLONE_DIALOG_WIDTH}
      closeOnBackdrop={!cloning}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={cloning}>
            Cancel
          </Button>
          <Button
            leftIcon={cloning ? undefined : <Copy className="h-4 w-4" />}
            onClick={() => void handleClone()}
            loading={cloning}
            disabled={nameMissing}
          >
            {cloning ? 'Cloning…' : 'Clone Group'}
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleClone();
        }}
      >
        <Field id="clone-group-name" label="New Group Name" required state={nameMissing ? 'error' : 'none'}>
          <Input
            id="clone-group-name"
            autoFocus
            placeholder="New group name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(undefined);
            }}
            error={nameError}
            className={fieldClasses.input(nameMissing ? 'error' : 'none')}
          />
        </Field>
        <Field
          id="clone-group-facility"
          label="Apply Facility (optional)"
          hint="When set, the cloned group and every cloned appointment type are assigned this facility."
        >
          <Select<number>
            id="clone-group-facility"
            options={facilityOptions}
            value={facilityId}
            onChange={(v) => setFacilityId(v ?? 0)}
          />
        </Field>
      </form>
    </Modal>
  );
}

// ─── Board row ─────────────────────────────────────────────────────────────────

function TypeRow({
  apptType,
  imageUrl,
  resourceMap,
  facilityLabel,
  deleted,
  onOpen,
}: {
  apptType: AppointmentTypeDetail;
  imageUrl: string | null;
  resourceMap: Record<string, string>;
  facilityLabel: string;
  deleted: boolean;
  onOpen: (t: AppointmentTypeDetail) => void;
}) {
  const resources = apptType.default_resources.map((r) => resourceMap[String(r.resource_id)] ?? `#${r.resource_id}`);

  return (
    <button
      type="button"
      // A deleted type is restored, not edited.
      disabled={deleted}
      onClick={() => onOpen(apptType)}
      className="flex w-full items-center gap-3 py-2 pr-2 text-left hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
    >
      <div className="w-8 h-8 rounded border border-slate-200 bg-slate-50 flex-shrink-0 flex items-center justify-center overflow-hidden">
        {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-4 w-4 text-slate-400" />}
      </div>
      <div className="min-w-0 flex-1">
        <span data-testid="type-name" className="block truncate font-medium text-sm text-slate-700">
          {apptType.name}
        </span>
        {resources.length > 0 && (
          <span className="block truncate text-xs text-slate-400">
            {resources.slice(0, 3).join(', ')}
            {resources.length > 3 ? ` +${resources.length - 3} more` : ''}
          </span>
        )}
      </div>
      <span className="flex w-12 shrink-0 justify-center">
        {apptType.color ? (
          <span
            className="inline-block h-4 w-4 rounded-full border border-slate-200"
            style={{ backgroundColor: apptType.color }}
            aria-hidden="true"
          />
        ) : (
          <span className="text-xs text-slate-300">—</span>
        )}
      </span>
      {/* Corp's two flag columns: has image, self-schedule. */}
      <span className="w-5 shrink-0" title={imageUrl ? 'Has an image' : undefined}>
        <FlagIcon on={Boolean(imageUrl)} icon={<ImageIcon />} className="text-green-500" label="Has an image" />
      </span>
      <span className="w-5 shrink-0" title={SELF_SCHEDULE_LABELS[apptType.addon?.self_schedule ?? 0]}>
        {getSelfScheduleIcon(apptType.addon?.self_schedule ?? 0)}
      </span>
      <span className="w-16 shrink-0 text-sm text-slate-600">{apptType.duration ? `${apptType.duration} min` : '—'}</span>
      <span className="hidden w-40 shrink-0 truncate text-xs text-slate-500 md:block">{facilityLabel}</span>
    </button>
  );
}

/** The board's sticky column header — the same widths as TypeRow. */
const TYPE_COLUMNS = (
  <div className="flex items-center gap-3 pr-2">
    <span className="w-8 shrink-0" aria-hidden="true" />
    <span className="min-w-0 flex-1">Name</span>
    <span className="w-12 shrink-0 text-center">Color</span>
    <span className="w-5 shrink-0" title="Has Image">
      <ImageIcon data-testid="image-column-icon" aria-label="Has Image" className="h-3.5 w-3.5 mx-auto text-slate-400" />
    </span>
    <span className="w-5 shrink-0" title="Self-Schedule">
      <CalendarCheck data-testid="self-schedule-column-icon" aria-label="Self-Schedule" className="h-3.5 w-3.5 mx-auto text-slate-400" />
    </span>
    <span className="w-16 shrink-0">Duration</span>
    <span className="hidden w-40 shrink-0 md:block">Facility</span>
  </div>
);

/** The corp Self-Schedule filter: addon.self_schedule 0–3 as strings. */
const SELF_SCHEDULE_FILTER_OPTIONS = [0, 1, 2, 3].map((v) => ({ value: String(v), label: SELF_SCHEDULE_LABELS[v] }));

/**
 * The corp Confirmation filter (SCRUM-2453), a filter on groups: "attached"
 * when the group's confirm_type_id is set, which is what the confirmation cron
 * sends from. A type takes its group's value; a type with no group gets no
 * confirmation, so it counts as "none".
 */
const CONFIRMATION_FILTER_OPTIONS = [
  { value: 'attached', label: 'Confirmation attached' },
  { value: 'none', label: 'No confirmation' },
];

function confirmationFilterValue(g: AppointmentTypeGroupRow | undefined): string {
  return g && Number(g.confirm_type_id) > 0 ? 'attached' : 'none';
}

/**
 * A group header's cells, right-aligned under the type columns: corp's image
 * and self-schedule summaries sit under their columns (Duration and Facility
 * are blank on a group row).
 */
function GroupColumns({ types, imageMap }: { types: AppointmentTypeDetail[]; imageMap: Record<string, string> }) {
  const live = types.filter((t) => !isTypeDeleted(t));
  const withImage = live.filter((t) => Boolean(imageMap[String(t.appointmentTypeID)])).length;
  return (
    <div className="flex shrink-0 items-center gap-3 pr-2">
      <span className="w-5 shrink-0">
        <SummaryIcon flagged={withImage} total={live.length} icon={<ImageIcon />} className="text-green-500" label="has an image" />
      </span>
      <span className="w-5 shrink-0">
        <GroupSelfScheduleIcon types={types} />
      </span>
      <span className="w-16 shrink-0" aria-hidden="true" />
      <span className="hidden w-40 shrink-0 md:block" aria-hidden="true" />
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

type TypeModalState = { kind: 'edit'; typeId: number } | { kind: 'new'; groupId: number | null } | null;
type GroupModalState = { kind: 'add' } | { kind: 'edit'; group: AppointmentTypeGroupRow } | null;

export function AppointmentSettingsAdmin() {
  // Locked: the shared blocks drop Add, the row icons and drag; the page only
  // stops talking about dragging and adding (silently, R18).
  const locked = useAdminPageLevel() === 'locked';
  const [types, setTypes] = useState<AppointmentTypeDetail[]>([]);
  const [groups, setGroups] = useState<AppointmentTypeGroupRow[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [lookups, setLookups] = useState<LookupMaps>({ resourceMap: {}, productTypeMap: {}, planTypeMap: {} });
  const [confirmTypes, setConfirmTypes] = useState<Array<{ value: number; label: string }>>([]);
  const [typeImageMap, setTypeImageMap] = useState<Record<string, string>>({});
  const [groupAttrs, setGroupAttrs] = useState<AppointmentTypeGroupAttr[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [typeModal, setTypeModal] = useState<TypeModalState>(null);
  const [groupModal, setGroupModal] = useState<GroupModalState>(null);
  const [cloningGroup, setCloningGroup] = useState<AppointmentTypeGroupRow | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<BoardCategory | null>(null);
  const [deletingType, setDeletingType] = useState<AppointmentTypeDetail | null>(null);
  // Off by default. On, deleted groups and types are listed too.
  const [showDeleted, setShowDeleted] = useState(false);
  // The corp toolbar's filters (nothing picked = no filtering). Ids are strings.
  const [facilityFilter, setFacilityFilter] = useState<string[]>([]);
  const [selfScheduleFilter, setSelfScheduleFilter] = useState<string[]>([]);
  const [confirmationFilter, setConfirmationFilter] = useState<string[]>([]);

  // ── Data loading ─────────────────────────────────────────────────────

  // Only the latest load may write: flipping "Show deleted" quickly must not
  // let an older answer land last.
  const loadSeq = useRef(0);

  /**
   * withDeleted adds the deleted types: the types index with no `active`
   * filter returns active and deleted together. Groups are always read both
   * ways (the index answers active-only or, with active=0, deleted-only) and
   * merged: even with Show deleted off the page must know which groups are
   * deleted, to hide their types rather than file them under "No group".
   * quiet keeps the board mounted (and its open groups open).
   */
  const load = useCallback(async (withDeleted: boolean, quiet = false) => {
    const seq = ++loadSeq.current;
    if (!quiet) setLoading(true);
    try {
      const [typeData, groupData, facilityData] = await Promise.all([
        withDeleted
          ? appointmentsService.listAllAppointmentTypesRaw({ includeDeleted: true })
          : appointmentsService.listAllAppointmentTypesRaw(),
        Promise.all([
          appointmentsService.listAppointmentTypeGroups(),
          appointmentsService.listAppointmentTypeGroups({ active: false }),
        ]).then(([live, deleted]) => mergeGroups(live, deleted)),
        adminSvc.listFacilities(),
      ]);
      if (seq !== loadSeq.current) return;
      setTypes(typeData);
      setGroups(groupData);
      setFacilities(facilityData);
    } catch {
      if (seq === loadSeq.current) toast.error('Failed to load appointment types');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  function handleShowDeletedChange(on: boolean) {
    setShowDeleted(on);
    void load(on, true);
  }

  useEffect(() => {
    void load(false);
    // Images and the modals' pickers. Each is optional: a failed lookup leaves its list empty.
    void Promise.allSettled([
      appointmentsService.getAppointmentTypeImages(),
      appointmentsService.getApptTypeLookups(),
      adminSvc.listAppointmentConfirmations(),
      appointmentsService.listAppointmentTypeGroupAttrs(),
    ]).then(([images, maps, confirms, attrs]) => {
      if (images.status === 'fulfilled' && images.value) setTypeImageMap(images.value);
      if (maps.status === 'fulfilled' && maps.value) setLookups(maps.value);
      if (attrs.status === 'fulfilled' && Array.isArray(attrs.value)) setGroupAttrs(attrs.value);
      if (confirms.status === 'fulfilled' && Array.isArray(confirms.value?.items)) {
        setConfirmTypes(
          confirms.value.items.map((c) => ({ value: c.appointmentConfirmTypeID, label: c.name || `Confirmation #${c.appointmentConfirmTypeID}` })),
        );
      }
    });
  }, [load]);

  // ── Board data ───────────────────────────────────────────────────────

  const sortedGroups = useMemo(() => [...groups].sort(compareGroups), [groups]);
  // The type modal's group picker offers live groups only.
  const activeGroups = useMemo(() => sortedGroups.filter((g) => !isGroupDeleted(g)), [sortedGroups]);
  const groupNameById = useMemo(() => new Map(groups.map((g) => [g.appointmentTypeGroupID, g.name])), [groups]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.appointmentTypeGroupID, g])), [groups]);

  const activeTypes = useMemo(() => types.filter((t) => !isTypeDeleted(t)).sort(compareTypes), [types]);

  const deletedGroupIds = useMemo(
    () => new Set(groups.filter(isGroupDeleted).map((g) => g.appointmentTypeGroupID)),
    [groups],
  );
  const separatorGroupIds = useMemo(
    () => new Set(groups.filter((g) => isSeparatorTitle(g.name)).map((g) => g.appointmentTypeGroupID)),
    [groups],
  );

  // Never shown: separator types, and types of a separator group. With Show
  // deleted off, also deleted types and types whose group is deleted (corp
  // rule); with it on, those sit under their deleted group.
  const listed = useMemo(() => {
    const inSeparatorGroup = (t: AppointmentTypeDetail) => {
      const g = getGroupId(t);
      return (g !== null && separatorGroupIds.has(g)) || isSeparatorTitle(t.group_name);
    };
    const shown = (t: AppointmentTypeDetail) => {
      if (isSeparatorTitle(t.name) || inSeparatorGroup(t)) return false;
      if (showDeleted) return true;
      const g = getGroupId(t);
      return !isTypeDeleted(t) && !(g !== null && deletedGroupIds.has(g));
    };
    return types.filter(shown).sort(compareTypes);
  }, [showDeleted, types, deletedGroupIds, separatorGroupIds]);

  const query = searchQuery.toLowerCase().trim();
  const searching = query !== '';

  // ── Filters (corp parity: Facility, Self-Schedule, Confirmation) ──────

  const filters: ListFilterConfig[] = [
    {
      id: 'appt-types-facility-filter',
      label: 'Filter by facility',
      placeholder: 'All facilities',
      searchPlaceholder: 'Search facilities…',
      emptyMessage: 'No facilities found.',
      options: facilities.filter((f) => f.active).map((f) => ({ value: String(f.facilityId), label: f.facilityName })),
      selected: facilityFilter,
      onChange: setFacilityFilter,
    },
    {
      id: 'appt-types-self-schedule-filter',
      label: 'Filter by self-schedule',
      placeholder: 'All self-schedule',
      options: SELF_SCHEDULE_FILTER_OPTIONS,
      selected: selfScheduleFilter,
      onChange: setSelfScheduleFilter,
    },
    {
      id: 'appt-types-confirmation-filter',
      label: 'Filter by confirmation',
      placeholder: 'All confirmations',
      options: CONFIRMATION_FILTER_OPTIONS,
      selected: confirmationFilter,
      onChange: setConfirmationFilter,
    },
  ];
  const filtering = anyFilterActive(filters);

  const records = useMemo(() => {
    if (!searching && !filtering) return listed;
    return listed.filter((t) => {
      if (searching) {
        const groupName = (t.group_id ? groupNameById.get(t.group_id) : '') ?? '';
        const hit =
          (t.name ?? '').toLowerCase().includes(query) ||
          descriptionToText(t.description).toLowerCase().includes(query) ||
          groupName.toLowerCase().includes(query);
        if (!hit) return false;
      }
      // A type with no facility (0) is offered at every facility, so it always passes.
      if (facilityFilter.length > 0 && t.facility_id && !facilityFilter.includes(String(t.facility_id))) return false;
      if (selfScheduleFilter.length > 0 && !selfScheduleFilter.includes(String(t.addon?.self_schedule ?? 0))) return false;
      if (confirmationFilter.length > 0) {
        const g = getGroupId(t);
        if (!confirmationFilter.includes(confirmationFilterValue(g === null ? undefined : groupById.get(g)))) return false;
      }
      return true;
    });
  }, [listed, query, searching, filtering, groupNameById, groupById, facilityFilter, selfScheduleFilter, confirmationFilter]);

  // A searched or filtered board shows only the groups holding a match. The
  // Confirmation filter is about groups, so when it is the only thing
  // narrowing, a matching group is listed even with no types (as corp lists it).
  const narrowed = searching || filtering;
  const onlyGroupFilter = !searching && facilityFilter.length === 0 && selfScheduleFilter.length === 0;
  const boardCategories = useMemo((): BoardCategory[] => {
    const all = sortedGroups
      .filter((g) => !isSeparatorTitle(g.name) && (showDeleted || !isGroupDeleted(g)))
      .map((g) => ({ id: g.appointmentTypeGroupID, title: g.name, deleted: isGroupDeleted(g) }));
    if (!narrowed) return all;
    const withMatches = new Set(records.map(getGroupId));
    const groupMatches = (id: number) =>
      onlyGroupFilter && confirmationFilter.includes(confirmationFilterValue(groupById.get(id)));
    return all.filter((c) => withMatches.has(c.id) || groupMatches(c.id));
  }, [sortedGroups, records, narrowed, showDeleted, onlyGroupFilter, confirmationFilter, groupById]);

  /** Each group's listed types, for the group-row self-schedule summary. */
  const typesByGroup = useMemo(() => {
    const map = new Map<number, AppointmentTypeDetail[]>();
    for (const t of records) {
      const g = getGroupId(t);
      if (g !== null) map.set(g, [...(map.get(g) ?? []), t]);
    }
    return map;
  }, [records]);

  /** Highest atOrder among the group's active types, plus one — a new or moved type lands last. */
  const nextTypeOrder = useCallback(
    (groupId: number) =>
      activeTypes.reduce((max, t) => (getGroupId(t) === groupId ? Math.max(max, Number(t.order) || 0) : max), 0) + 1,
    [activeTypes],
  );

  const nextGroupOrder = useMemo(() => groups.reduce((max, g) => Math.max(max, Number(g.order) || 0), 0) + 1, [groups]);

  // ── Board saves (each returns the API promise; the board rolls back on reject)

  const handleReorderRecords = useCallback(
    async (groupId: number, ids: number[]) => {
      await appointmentsService.reorderAppointmentTypes(groupId, ids);
      const groupName = groupNameById.get(groupId) ?? '';
      setTypes((prev) =>
        prev.map((t) => {
          const at = ids.indexOf(t.appointmentTypeID);
          return at < 0 ? t : { ...t, group_id: groupId, group_name: groupName, order: at + 1 };
        }),
      );
    },
    [groupNameById],
  );

  const handleSaveError = useCallback(() => {
    toast.error('The new order could not be saved.');
  }, []);

  // ── Local state patchers ─────────────────────────────────────────────

  function handleSelfScheduleChanged(typeId: number, value: number) {
    setTypes((prev) =>
      prev.map((t) => (t.appointmentTypeID === typeId ? { ...t, addon: { ...t.addon, self_schedule: value } } : t)),
    );
  }

  /**
   * A type's image was assigned: show it at once, then re-read the image map
   * (merged, so a read that lags the assignment cannot drop it).
   */
  function handleImageChanged(typeId: number, url: string) {
    setTypeImageMap((prev) => ({ ...prev, [String(typeId)]: url }));
    appointmentsService
      .getAppointmentTypeImages()
      .then((fresh) => {
        if (fresh) setTypeImageMap((prev) => ({ ...prev, ...fresh }));
      })
      .catch(() => {});
  }

  function handleTypeSaved(saved: AppointmentTypeDetail, isNew: boolean) {
    if (saved && typeof saved.appointmentTypeID === 'number') {
      setTypes((prev) =>
        isNew ? [...prev, saved] : prev.map((t) => (t.appointmentTypeID === saved.appointmentTypeID ? { ...t, ...saved } : t)),
      );
    } else {
      void load(showDeleted, true);
    }
    setTypeModal(null);
  }

  /** Off the board — or, while deleted rows are shown, marked deleted in place. */
  function handleTypeDeleted(typeId: number) {
    setTypes((prev) =>
      showDeleted
        ? prev.map((t) => (t.appointmentTypeID === typeId ? { ...t, active: false, deleted: true } : t))
        : prev.filter((t) => t.appointmentTypeID !== typeId),
    );
    setTypeModal(null);
  }

  async function handleDeleteType() {
    if (!deletingType) return;
    const id = deletingType.appointmentTypeID;
    try {
      await appointmentsService.deleteAppointmentType(id);
      toast.success('Appointment type deleted');
      handleTypeDeleted(id);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The appointment type could not be deleted.'));
    }
  }

  async function handleRestoreType(t: AppointmentTypeDetail) {
    try {
      await appointmentsService.restoreAppointmentType(t.appointmentTypeID);
      toast.success('Appointment type restored');
      await load(showDeleted, true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The appointment type could not be restored.'));
    }
  }

  async function handleRestoreGroup(c: BoardCategory) {
    try {
      await appointmentsService.restoreAppointmentTypeGroup(c.id);
      toast.success('Group restored');
      await load(showDeleted, true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The group could not be restored.'));
    }
  }

  /** A group was added or updated (from the board, or from the type dialog's Group field). */
  function applyGroupSaved(saved: AppointmentTypeGroupRow, isNew: boolean) {
    if (saved && typeof saved.appointmentTypeGroupID === 'number') {
      setGroups((prev) => {
        if (isNew) return [...prev, { ...saved, order: saved.order || nextGroupOrder }];
        return prev.map((g) => (g.appointmentTypeGroupID === saved.appointmentTypeGroupID ? { ...g, ...saved, order: g.order } : g));
      });
    } else {
      void load(showDeleted, true);
    }
    toast.success(isNew ? 'Group added' : 'Group saved');
  }

  function handleGroupSaved(saved: AppointmentTypeGroupRow, isNew: boolean) {
    applyGroupSaved(saved, isNew);
    setGroupModal(null);
  }

  /** Active types filed under a group (the group dialog's read-only list; the ones a clone copies). */
  const typesOfGroup = useCallback(
    (groupId: number) => activeTypes.filter((t) => getGroupId(t) === groupId),
    [activeTypes],
  );

  function typesIn(groupId: number): number {
    return typesOfGroup(groupId).length;
  }

  function handleGroupCloned() {
    setCloningGroup(null);
    void load(showDeleted, true);
  }

  async function handleDeleteGroup() {
    if (!deletingGroup) return;
    const id = deletingGroup.id;
    try {
      await appointmentsService.deleteAppointmentTypeGroup(id);
      // Marked deleted in place (the page always holds deleted groups): with
      // Show deleted off it leaves the board and its types are hidden with it.
      setGroups((prev) =>
        prev.map((g) => (g.appointmentTypeGroupID === id ? { ...g, active: false, deleted: true } : g)),
      );
      toast.success('Group deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The group could not be deleted.'));
    }
  }

  // ── Render ───────────────────────────────────────────────────────────

  const facilityLabel = useCallback(
    (facilityId: number | null) => {
      if (!facilityId) return 'All facilities';
      return facilities.find((f) => f.facilityId === facilityId)?.facilityName ?? `Facility #${facilityId}`;
    },
    [facilities],
  );

  const renderRecord = useCallback(
    (t: AppointmentTypeDetail) => (
      <TypeRow
        apptType={t}
        imageUrl={typeImageMap[String(t.appointmentTypeID)] || null}
        resourceMap={lookups.resourceMap}
        facilityLabel={facilityLabel(t.facility_id)}
        deleted={isTypeDeleted(t)}
        onOpen={(row) => setTypeModal({ kind: 'edit', typeId: row.appointmentTypeID })}
      />
    ),
    [typeImageMap, lookups.resourceMap, facilityLabel],
  );

  // A deleted group offers only Restore.
  function renderCategoryActions(c: BoardCategory) {
    if (c.deleted) {
      return <AdminRowAction kind="restore" label={`Restore ${c.title}`} onClick={() => void handleRestoreGroup(c)} />;
    }
    const groupRow = () => groups.find((row) => row.appointmentTypeGroupID === c.id);
    return (
      <>
        <AdminRowAction
          kind="add"
          title="Add appointment type"
          label={`Add appointment type to ${c.title}`}
          onClick={() => setTypeModal({ kind: 'new', groupId: c.id })}
        />
        {/* A group row only expands; its dialog holds more than the header
            (facility, confirmation, class, shortcodes), so Locked keeps a View. */}
        <AdminRowAction
          kind="edit"
          label={`Edit ${c.title}`}
          viewWhenLocked
          onClick={() => {
            const g = groupRow();
            if (g) setGroupModal({ kind: 'edit', group: g });
          }}
        />
        <AdminRowAction
          kind="clone"
          label={`Clone ${c.title}`}
          onClick={() => {
            const g = groupRow();
            if (g) setCloningGroup(g);
          }}
        />
        <AdminRowAction kind="delete" label={`Delete ${c.title}`} onClick={() => setDeletingGroup(c)} />
      </>
    );
  }

  // A deleted type offers only Restore.
  function renderRecordActions(t: AppointmentTypeDetail) {
    if (isTypeDeleted(t)) {
      return (
        <AdminRowAction kind="restore" label={`Restore appointment type ${t.name}`} onClick={() => void handleRestoreType(t)} />
      );
    }
    return (
      <>
        <AdminRowAction
          kind="edit"
          label={`Edit appointment type ${t.name}`}
          onClick={() => setTypeModal({ kind: 'edit', typeId: t.appointmentTypeID })}
        />
        <AdminRowAction kind="delete" label={`Delete appointment type ${t.name}`} onClick={() => setDeletingType(t)} />
      </>
    );
  }

  // The modal's type is read from state, so the self-schedule save shows there too.
  const editing = typeModal?.kind === 'edit' ? types.find((t) => t.appointmentTypeID === typeModal.typeId) ?? null : null;

  return (
    <AdminBoardPage
      title="Appointment Types"
      subtitle={
        locked
          ? 'Appointment types grouped by group.'
          : 'Appointment types grouped by group. Drag a type onto another group to move it there.'
      }
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setGroupModal({ kind: 'add' })}>
          Add group
        </Button>
      }
      toolbar={
        <AdminListToolbar
          className="mb-0"
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by name, description, group…"
          searchLabel="Search appointment types"
          showDeleted={showDeleted}
          onShowDeletedChange={handleShowDeletedChange}
          showDeletedId="appt-types-show-deleted"
        >
          <ListFilterBar filters={filters} />
        </AdminListToolbar>
      }
      hint={boardDragHint({ searching, filtering, showDeleted })}
      dialogs={
        <>
          {typeModal && (typeModal.kind === 'new' || editing) && (
            <TypeModal
              key={typeModal.kind === 'edit' ? `edit-${typeModal.typeId}` : 'new'}
              apptType={editing}
              defaultGroupId={typeModal.kind === 'new' ? typeModal.groupId : null}
              groups={activeGroups}
              facilities={facilities}
              lookups={lookups}
              imageUrl={editing ? typeImageMap[String(editing.appointmentTypeID)] || null : null}
              nextOrder={nextTypeOrder}
              confirmTypes={confirmTypes}
              attrs={groupAttrs}
              nextGroupOrder={nextGroupOrder}
              typesOf={typesOfGroup}
              onClose={() => setTypeModal(null)}
              onSaved={handleTypeSaved}
              onSelfScheduleChanged={handleSelfScheduleChanged}
              onImageChanged={handleImageChanged}
              onGroupSaved={applyGroupSaved}
            />
          )}
    
          {groupModal && (
            <GroupModal
              key={groupModal.kind === 'edit' ? `edit-${groupModal.group.appointmentTypeGroupID}` : 'add'}
              group={groupModal.kind === 'edit' ? groupModal.group : null}
              facilities={facilities}
              confirmTypes={confirmTypes}
              attrs={groupAttrs}
              liveGroups={activeGroups}
              nextOrder={nextGroupOrder}
              typesOf={typesOfGroup}
              onClose={() => setGroupModal(null)}
              onSaved={handleGroupSaved}
            />
          )}
    
          {cloningGroup && (
            <CloneGroupModal
              key={cloningGroup.appointmentTypeGroupID}
              source={cloningGroup}
              typeCount={typesIn(cloningGroup.appointmentTypeGroupID)}
              facilities={facilities}
              liveGroups={activeGroups}
              onClose={() => setCloningGroup(null)}
              onCloned={handleGroupCloned}
            />
          )}
    
          <ConfirmDialog
            isOpen={deletingGroup !== null}
            onClose={() => setDeletingGroup(null)}
            onConfirm={handleDeleteGroup}
            title="Delete group?"
            message={deletingGroup ? `Delete "${deletingGroup.title}"? Its appointment types will be hidden.` : ''}
            confirmLabel="Delete group"
          />
    
          <ConfirmDialog
            isOpen={deletingType !== null}
            onClose={() => setDeletingType(null)}
            onConfirm={handleDeleteType}
            title="Delete appointment type?"
            message={`${deletingType?.name ?? 'This appointment type'} will no longer be offered for booking. Existing appointments are not changed.`}
            confirmLabel="Delete"
          />
        </>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading appointment types…" />
      ) : narrowed && records.length === 0 && boardCategories.length === 0 ? (
        <AdminListEmpty title="No appointment types found" hint="Try adjusting your search or filters." />
      ) : (
        <>
          {!narrowed && boardCategories.length === 0 && records.length === 0 && (
            <AdminListEmpty title="No groups yet" hint={locked ? undefined : 'Add a group, then add appointment types to it.'} />
          )}
          <CategoryBoard<AppointmentTypeDetail>
            categories={boardCategories}
            records={records}
            getRecordId={getRecordId}
            getRecordTitle={getTypeTitle}
            getRecordCategoryId={getGroupId}
            renderRecord={renderRecord}
            columnHeader={TYPE_COLUMNS}
            renderCategoryColumns={(c) => <GroupColumns types={typesByGroup.get(c.id) ?? []} imageMap={typeImageMap} />}
            renderCategoryActions={renderCategoryActions}
            renderRecordActions={renderRecordActions}
            isRecordDeleted={isTypeDeleted}
            onReorderRecords={handleReorderRecords}
            onSaveError={handleSaveError}
            recordNoun={['type', 'types']}
            uncategorizedLabel="No group"
            // A list holding deleted rows (or a searched / filtered one) is never saved as an order.
            dragDisabled={narrowed || showDeleted}
          />
        </>
      )}
    </AdminBoardPage>
  );
}
